import { useStatusOverlay } from '@fs/zion-ui'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useZionAxios } from '@fs/zion-axios'
import { type GroupMember, type GroupId } from '@fs/zion-tree-types'
import { useUserIsDataAdmin } from '../providers/DataAdminProvider'

type ReturnData = Omit<ReturnType<typeof useZionAxios>[0], 'data'> & {
  refetch: ReturnType<typeof useZionAxios>[1]
  data: {
    members: GroupMember[]
    pendingMembers: GroupMember[]
  }
}

export default function useGroupMembers(groupId: GroupId): ReturnData {
  const [t] = useTranslation()
  const showStatusOverlay = useStatusOverlay()
  const url = groupId ? `/service/tree/tree-data/groups/${groupId}/members` : undefined
  const [{ data, loading, error }, refetch] = useZionAxios<{ members: GroupMember[] }>({ url })
  const { userIsDataAdmin } = useUserIsDataAdmin()

  useEffect(() => {
    if (error) {
      console.error(error)
      if (error.status === 410 && userIsDataAdmin) {
        showStatusOverlay({
          type: 'error',
          message: t('This group has been deleted.'),
        })
      } else {
        showStatusOverlay({ type: 'error', message: t('group-management.group-members.error') })
      }
    }
  }, [error, showStatusOverlay, t, userIsDataAdmin])

  const members = (data?.members ?? []).filter((member) => ['ADMIN', 'MEMBER'].includes(member.status))
  const pendingMembers = (data?.members ?? []).filter((member) => member.status === 'PENDING')

  return { data: { members, pendingMembers }, loading, error, refetch }
}
